import { useState, useEffect } from 'react';
import clsx from 'clsx';
import { Icon } from './Icon';
import { Link } from './Link';
import { Heading } from './Heading';

// 예시 데이터 타입 정의
interface MenuItemType {
  id: string | number;
  label: string;
  href?: string;
  to?: string;
  icon?: string;
  subMenu?: MenuItemType[];
}

// 예시 데이터
const TemporaryItems: MenuItemType[] = [
  {
    id: 1,
    label: 'Menu 1',
    href: '',
    subMenu: [
      { id: '1-1', label: 'Submenu 1-1', href: '' },
      { id: '1-2', label: 'Submenu 1-2', href: '' },
      { id: '1-3', label: 'Submenu 1-3', href: '' },
    ],
  },
  {
    id: 2,
    label: 'Menu 2',
    href: '',
    subMenu: [
      { id: '2-1', label: 'Submenu 2-1', href: '', subMenu: [{ id: '2-1-1', label: 'Submenu 2-1-1', href: '' }] },
      { id: '2-2', label: 'Submenu 2-2', href: '' },
    ],
  },
];

// 메뉴 컴포넌트 (재귀 렌더링)
interface MenuProps {
  data: MenuItemType[];
  depth?: number;
  currentMenu: string | number;
  expandedMenus: Array<string | number>;
  onMenuClick: (item: MenuItemType, hasSubMenu: boolean, depth: number) => void;
}

const Menu: React.FC<MenuProps> = ({ data = [], depth = 0, currentMenu, expandedMenus, onMenuClick }) => {
  return (
    <ul className="dsx-NavSide-menu" data-depth={depth} data-state={expandedMenus ? 'show' : 'hide'}>
      {data.map((item) => (
        <MenuItem
          key={item.id}
          item={item}
          depth={depth}
          currentMenu={currentMenu}
          expandedMenus={expandedMenus}
          onMenuClick={onMenuClick}
        />
      ))}
    </ul>
  );
};

interface MenuItemProps {
  item: MenuItemType;
  depth: number;
  currentMenu: string | number;
  expandedMenus: Array<string | number>;
  onMenuClick: (item: MenuItemType, hasSubMenu: boolean, depth: number) => void;
}

const MenuItem: React.FC<MenuItemProps> = ({ item, depth, currentMenu, expandedMenus, onMenuClick }) => {
  const hasSubMenu = item.subMenu && item.subMenu.length > 0;
  const isActive = currentMenu === item.id;
  const isExpanded = expandedMenus.includes(item.id) && !isActive;
  const isSubActive = (item: MenuItemType): boolean =>
    item.subMenu?.some((subItem) => subItem.id === currentMenu || isSubActive(subItem)) ?? false;

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault(); console.log(e); 
    //if (hasSubMenu as boolean) onMenuClick(item, hasSubMenu as boolean, depth);
    onMenuClick(item, hasSubMenu as boolean, depth);
  };

  return (
    <li className={clsx('menu-item', { 'is-expanded': isExpanded })}>
      <Link
        role="button"
        to={item.to}
        href={item.href ?? '#'}
        className={clsx('menu-link', { 'is-active': isActive ?? isSubActive(item) })}
        // className={clsx('menu-link', { 'is-active': isActive ?? false })}
        onClick={handleClick}
        aria-selected={isActive ? 'true' : 'false'}
        aria-expanded={hasSubMenu ? isExpanded : undefined}
      >
        { <Icon name={item.icon ?? 'expand'} />}
        <span>{item.label}</span>
        {hasSubMenu && <Icon name="chevronBottom" />}
      </Link>

      {hasSubMenu && (
        <Menu
          data={item.subMenu as MenuItemType[]} // Make sure to pass the submenu here
          depth={depth + 1}
          currentMenu={currentMenu}
          expandedMenus={expandedMenus}
          onMenuClick={onMenuClick}
        />
      )}
    </li>
  );
};

// 내비게이션 사이드 컴포넌트
interface NavSideProps {
  menuName?: string;
  menuItems?: MenuItemType[];
  className?: string;
  initialActiveMenu?: string | number;
  onClick?: (item: MenuItemType) => void;
}

export const NavSide: React.FC<NavSideProps> = ({
  menuName = 'Navigation',
  menuItems = TemporaryItems,
  className,
  initialActiveMenu,
  onClick,
}) => {
  const [currentMenu, setCurrentMenu] = useState<string | number>(initialActiveMenu ?? '임시');
  const [expandedMenus, setExpandedMenus] = useState<Array<string | number>>([]);

  useEffect(() => {
    if (initialActiveMenu) {
      const expandParents = (items: MenuItemType[], targetId: string | number): Array<string | number> => {
        for (const item of items) {
          if (item.id === targetId) return [item.id];
          if (item.subMenu) {
            const subPath = expandParents(item.subMenu, targetId);
            if (subPath.length) return [item.id, ...subPath];
          }
        }
        return [];
      };
      const expanded = expandParents(menuItems, initialActiveMenu);
      setExpandedMenus(expanded);
      setCurrentMenu(initialActiveMenu);
    }
  }, [initialActiveMenu]);

  const handleMenuClick = (item: MenuItemType, hasSubMenu: boolean, depth: number) => {
    if (hasSubMenu) {
      const subMenuIds = collectSubMenuIds(item.subMenu ?? []);
      const isMenuExpanded = expandedMenus.includes(item.id);

      const newExpandedMenus = expandedMenus.filter((id) => {
        const menuDepth = findMenuDepth(menuItems, id);
        return menuDepth !== depth && !subMenuIds.includes(id);
      });

      isMenuExpanded ? setExpandedMenus(newExpandedMenus) : setExpandedMenus([...newExpandedMenus, item.id]);
    } else {
      setCurrentMenu(item.id);
    }
    if (onClick) onClick(item);
  };

  const collectSubMenuIds = (items: MenuItemType[]): Array<string | number> => {
    let ids: Array<string | number> = [];

    items.forEach((sub) => {
      ids.push(sub.id);
      if (sub.subMenu) ids = ids.concat(collectSubMenuIds(sub.subMenu));
    });

    return ids;
  };

  const findMenuDepth = (items: MenuItemType[], id: string | number, depth = 0): number => {
    for (const item of items) {
      if (item.id === id) return depth;
      if (item.subMenu) {
        const subDepth = findMenuDepth(item.subMenu, id, depth + 1);
        if (subDepth >= 0) return subDepth;
      }
    }
    return -1; // 찾지 못한 경우
  };

  return (
    <nav className={clsx('dsx-NavSide', className)}>
      {menuName && (
        <Heading as="strong" size="title2" weight="semibold">
          {menuName}
        </Heading>
      )}
      <Menu data={menuItems} currentMenu={currentMenu} expandedMenus={expandedMenus} onMenuClick={handleMenuClick} />
    </nav>
  );
};
